import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import useFetch from "@/modules/hooks/use-fetch";
// import useToast from "@/modules/hooks/use-toast";
import { api } from "@/utils/index";
import getCSRFToken from "../../utils/csrf";
// polyglot not needed in this module

export const getZipCodes = (data) => {
  getCSRFToken();
  return axios.get("/cities/autocomplete_city", { params: data });
};

export const getAddresses = (data) => {
  getCSRFToken();
  return axios.get("/cities/autocomplete_address", { params: data });
};

export const getAddressDetails = (data) => {
  getCSRFToken();
  return axios.get("/cities/details_address", { params: data });
};

export const useGetZipCode = (values, { enabled }) => {
  const queryClient = useQueryClient();
  const queryKey = ["/cities/autocomplete_city"];
  const res = useFetch("/cities/autocomplete_city", {
    params: values,
    enabled,
    queryKey,
    retry: false,
    shadow: true,
    refetchOnWindowFocus: false,
  });
  if (!values.term) {
    queryClient.removeQueries({ queryKey });
    return { ...res, data: null };
  }
  return res;
};

export const useGetZipCodeDetails = () =>
  useMutation<unknown, unknown, { place_id: string }>({
    mutationFn: ({ place_id }) =>
      api.get("/cities/details_city", {
        params: { place_id },
      }),
  });

export type AddressAutocompleteItem = { id: string; description: string };

export const useGetAddresses = () =>
  useMutation<{ data: AddressAutocompleteItem[] }, Error, { term: string; session_token: string }>({
    mutationFn: (values) =>
      api.get("/cities/autocomplete_address", {
        params: values,
      }),
    onError: () => null,
    retry: false,
  });

export const useGetAddressesQuery = (values, { enabled }) => {
  const queryKey = ["/cities/autocomplete_address_query"];
  return useFetch("/cities/autocomplete_address", {
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 20 * 1000, // 20 seconds
    params: values,
    enabled,
    queryKey,
    retry: false,
    shadow: true,
    refetchOnWindowFocus: false,
    apiBackwardCompatibility: true,
  });
};

type AddressDetailsResponse = { coordinates: string };

export const useGetAddressDetails = ({
  onSuccess,
  onError,
}: {
  onSuccess: (v: {
    place_id: string;
    label: string;
    location: { latitude: string; longitude: string };
  }) => void;
  onError?: (err: unknown) => void;
}) =>
  useMutation<
    { data: AddressDetailsResponse },
    Error,
    { place_id: string | number; session_token: string; label: string }
  >({
    mutationFn: ({ place_id, session_token }) =>
      api.get("/cities/details_address", {
        params: { place_id, session_token },
      }),
    onSuccess: ({ data }, { place_id, label }) => {
      const coordinates = data.coordinates.split(",");
      const latitude = coordinates[0];
      const longitude = coordinates[1];
      onSuccess({
        place_id: place_id.toString(),
        label,
        location: { latitude, longitude },
      });
    },
    onError,
  });
